/**
 * useMessages Hook
 * Manages chat message state with two data structures:
 * 1. Conversation history (OpenAI format): [{ role, content, timestamp }]
 * 2. UI messages (for rendering): [{ component, type, data }]
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { COMPONENT_TYPES, MESSAGE_LIMITS, MESSAGE_ROLES, isDisplayableRole } from '../constants';

/**
 * Generate a unique message ID
 * @returns {string}
 */
const generateMessageId = () => `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/**
 * Hook for managing chat messages
 * @param {Object} chatService - Chat service instance with on/off methods
 * @returns {Object} Messages state and actions
 */
const useMessages = (chatService) => {
    // Conversation history (OpenAI format for API)
    const [conversationHistory, setConversationHistory] = useState([]);

    // UI messages (for rendering)
    const [uiMessages, setUIMessages] = useState([]);

    // Loading/error state
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Track current streaming message
    const streamingMessageRef = useRef(null);

    // Track if step indicator is showing
    const [showStepIndicator, setShowStepIndicator] = useState(false);
    const [stepIndicatorText, setStepIndicatorText] = useState('Thinking');

    useEffect(() => {
        if (!chatService?.on) return;

        // Handle run started - show step indicator
        const unsubRunStart = chatService.on('runStarted', () => {
            setShowStepIndicator(true);
            setStepIndicatorText('Thinking');
            setIsLoading(true);
            setError(null);
        });

        // Handle step started
        const unsubStepStart = chatService.on('stepStarted', ({ stepName }) => {
            setShowStepIndicator(true);
            setStepIndicatorText(stepName || 'Thinking');
        });

        // Handle text message start - hide step indicator, add streaming message
        const unsubMsgStart = chatService.on('textMessageStart', ({ messageId, role }) => {
            if (!isDisplayableRole(role)) return;

            // Hide step indicator when first content arrives
            setShowStepIndicator(false);

            const id = messageId || generateMessageId();
            streamingMessageRef.current = id;

            // Add to UI messages
            setUIMessages(prev => [...prev, {
                id,
                component: role === MESSAGE_ROLES.USER
                    ? COMPONENT_TYPES.USER_MESSAGE
                    : COMPONENT_TYPES.ASSISTANT_MESSAGE,
                type: 'markdown',
                data: '',
                isStreaming: true,
                timestamp: Date.now()
            }]);

            setIsLoading(false);
        });

        // Handle content updates
        const unsubContent = chatService.on('textMessageContent', ({ messageId, fullContent }) => {
            const targetId = messageId || streamingMessageRef.current;

            setUIMessages(prev => prev.map(msg =>
                msg.id === targetId
                    ? { ...msg, data: fullContent }
                    : msg
            ));
        });

        // Handle message end - set isComplete and remove streaming flag
        const unsubMsgEnd = chatService.on('textMessageEnd', ({ messageId, content, role }) => {
            const targetId = messageId || streamingMessageRef.current;

            // Update UI message - mark as complete, stop streaming
            setUIMessages(prev => prev.map(msg =>
                msg.id === targetId
                    ? { ...msg, data: content || msg.data, isStreaming: false, isComplete: true }
                    : msg
            ));

            // Add to conversation history
            if (role && content) {
                setConversationHistory(prev => [...prev, {
                    role,
                    content,
                    timestamp: Date.now()
                }]);
            }

            streamingMessageRef.current = null;
            setShowStepIndicator(false);
        });

        // Handle run finished
        const unsubRunEnd = chatService.on('runFinished', () => {
            setIsLoading(false);
            setShowStepIndicator(false);
        });

        // Handle run error
        const unsubError = chatService.on('runError', ({ message }) => {
            setIsLoading(false);
            setShowStepIndicator(false);
            setError(message || 'An error occurred');

            // Mark streaming message as errored if present
            if (streamingMessageRef.current) {
                setUIMessages(prev => prev.map(msg =>
                    msg.id === streamingMessageRef.current
                        ? { ...msg, isStreaming: false, error: true }
                        : msg
                ));
                streamingMessageRef.current = null;
            }
        });

        return () => {
            unsubRunStart?.();
            unsubStepStart?.();
            unsubMsgStart?.();
            unsubContent?.();
            unsubMsgEnd?.();
            unsubRunEnd?.();
            unsubError?.();
        };
    }, [chatService]);

    // Add a user message
    const addUserMessage = useCallback((content) => {
        const trimmedContent = content.trim();
        if (!trimmedContent) return null;

        // Enforce message length limit
        const limitedContent = trimmedContent.slice(0, MESSAGE_LIMITS.MAX_MESSAGE_LENGTH);
        const id = generateMessageId();
        const timestamp = Date.now();

        // Add to conversation history
        setConversationHistory(prev => {
            const updated = [...prev, {
                role: MESSAGE_ROLES.USER,
                content: limitedContent,
                timestamp
            }];
            // Enforce history limit
            if (updated.length > MESSAGE_LIMITS.MAX_HISTORY_LENGTH) {
                return updated.slice(-MESSAGE_LIMITS.MAX_HISTORY_LENGTH);
            }
            return updated;
        });

        // Add to UI messages
        setUIMessages(prev => [...prev, {
            id,
            component: COMPONENT_TYPES.USER_MESSAGE,
            type: 'text',
            data: limitedContent,
            timestamp
        }]);

        // Show step indicator immediately after user sends message
        setShowStepIndicator(true);
        setStepIndicatorText('Thinking');
        setIsLoading(true);
        setError(null);

        return { id, content: limitedContent, timestamp };
    }, []);

    // Add step indicator
    const addStepIndicator = useCallback((text = 'Thinking') => {
        setShowStepIndicator(true);
        setStepIndicatorText(text);
    }, []);

    // Remove step indicator
    const removeStepIndicator = useCallback(() => {
        setShowStepIndicator(false);
    }, []);

    // Clear all messages
    const clearMessages = useCallback(() => {
        setConversationHistory([]);
        setUIMessages([]);
        setIsLoading(false);
        setError(null);
        setShowStepIndicator(false);
        streamingMessageRef.current = null;
    }, []);

    // Get messages formatted for sending to backend
    const getMessagesForBackend = useCallback(() => {
        return conversationHistory.map(m => ({
            role: m.role,
            content: m.content
        }));
    }, [conversationHistory]);

    // Get UI messages with step indicator if showing
    const getUIMessagesWithIndicator = useCallback(() => {
        if (showStepIndicator) {
            return [
                ...uiMessages,
                {
                    id: 'step-indicator',
                    component: COMPONENT_TYPES.STEP_INDICATOR,
                    type: 'text',
                    data: { step: stepIndicatorText },
                    timestamp: Date.now()
                }
            ];
        }
        return uiMessages;
    }, [uiMessages, showStepIndicator, stepIndicatorText]);

    // Set error state
    const setErrorState = useCallback((errorMessage) => {
        setError(errorMessage);
    }, []);

    return {
        // State
        conversationHistory,
        uiMessages: getUIMessagesWithIndicator(),
        isLoading,
        error,
        showStepIndicator,

        // Derived state - hasMessages based on actual messages, not step indicator
        hasMessages: uiMessages.length > 0,
        lastMessage: uiMessages[uiMessages.length - 1] || null,

        // Actions
        addUserMessage,
        addStepIndicator,
        removeStepIndicator,
        clearMessages,
        getMessagesForBackend,
        setError: setErrorState
    };
};

export default useMessages;